import { createTheme, ThemeOptions } from "@mui/material/styles";
import { merge } from "lodash";

import type { AppTheme, AppThemeProperties } from "./types";

/**
 * The `body` font stack from `./global.css`. MUI's CssBaseline is inserted
 * after `global.css`, so it sets the same stack itself. Keep the two in sync
 * (a unit test checks this).
 */
export const GLOBAL_CSS_BODY_FONT_FAMILY = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  '"Roboto"',
  '"Oxygen"',
  '"Ubuntu"',
  '"Cantarell"',
  '"Fira Sans"',
  '"Droid Sans"',
  '"Helvetica Neue"',
  "sans-serif",
].join(", ");

/**
 * Builds the single MUI theme for the app theme properties.
 *
 * The returned object is both the MUI theme (read by MUI components) and the
 * emotion theme (read by `css={(theme: AppTheme) => ...}`), since MUI's
 * `ThemeProvider` also fills emotion's theme context.
 */
export function createAppMuiTheme(appTheme: AppThemeProperties): AppTheme {
  const baseComponents: ThemeOptions["components"] = {
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: appTheme.mediaQueryNoSSR,
      },
    },
    MuiButton: {
      defaultProps: {
        size: appTheme.defaultButtonSize,
      },
    },
    // 14px body text, not the `body1` variant's 18px `allVariants` font
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: GLOBAL_CSS_BODY_FONT_FAMILY,
          fontWeight: 400,
          fontSize: "0.875rem",
          lineHeight: 1.43,
          letterSpacing: "normal",
        },
      },
    },
    // 12px helper text, not the app's `caption` font
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily: appTheme.fonts.main,
          fontWeight: 400,
          fontSize: "0.75rem",
          lineHeight: 1.66,
          letterSpacing: "normal",
        },
      },
    },
    // MUI focuses the paper when a dialog opens: hide the browser focus ring
    MuiDialog: {
      styleOverrides: {
        paper: {
          outline: 0,
        },
      },
    },
    // adornments take the input's color, not grey `palette.action.active`
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: "inherit",
        },
      },
    },
    // white text and icons on every filled alert (contrast text is dark on
    // success and warning)
    MuiAlert: {
      styleOverrides: {
        filled: {
          color: "#fff",
        },
      },
    },
  };

  const muiTheme = createTheme({
    // Deep merge so theme-level `muiComponents` (e.g. HHAX button variants)
    // extend the defaults above instead of replacing whole component entries.
    components: merge({}, baseComponents, appTheme.muiComponents),
    palette: appTheme.palette,
    shape: appTheme.shape,
    // Components without a typography variant (Tooltip, Chip, ...) read this
    // font family. A custom one also stops MUI adding Roboto letter-spacing.
    typography: {
      fontFamily: appTheme.fonts.main,
      ...appTheme.typography,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 500,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  });

  return {
    ...muiTheme,
    buttonBorderRadius: appTheme.buttonBorderRadius,
    defaultTextFieldSize: appTheme.defaultTextFieldSize,
    embedTheme: appTheme.embedTheme,
    maxContentWidth: appTheme.maxContentWidth,
    palette: {
      ...muiTheme.palette,
      gold: appTheme.palette.gold,
    },
  };
}
